import { useEffect, useRef, useState } from 'react';

export type HashNavigationUIProps<T> = {
  stepForHash: (hash: string) => number;
  renderStep: (step: number, state: T) => JSX.Element;
  maxAllowedStep: number;
  state: T;
  onStepChange: (step: number) => void;
};

/**
 * A very simple hash navigation wrapper which handles the `hashchange` event
 * and lets the user render any UI element based on a given step. The user is
 * responsible for parsing a URL hash into a step index.
 *
 * This will also trigger a redirect if the calculated step is greater than the
 * maximum allowed step.
 */
export const HashNavigation = <T,>({
  stepForHash,
  renderStep,
  maxAllowedStep,
  state,
  onStepChange,
}: HashNavigationUIProps<T>) => {
  const onHashCallback = useRef<((e: Event) => void) | null>(null);

  const [curHash, setCurHash] = useState(getLocationHash());

  useEffect(() => {
    // Executes whenever the current hash changes
    onStepChange(stepForHash(getLocationHash()));

    // because we only have a hash change in the URL, we need to handle the scrolling to the top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [curHash, onStepChange, stepForHash]);

  useEffect(() => {
    if (onHashCallback.current) {
      window.removeEventListener('hashchange', onHashCallback.current);
    }

    onHashCallback.current = (e: Event) => {
      e.preventDefault();
      setCurHash(getLocationHash());
    };

    window.addEventListener('hashchange', onHashCallback.current);

    return () => {
      if (onHashCallback.current) {
        window.removeEventListener('hashchange', onHashCallback.current);
      }
    };
  }, [setCurHash]);

  let step = stepForHash(curHash);

  if (step > maxAllowedStep) {
    step = maxAllowedStep;

    // If the current URL hash represents a step we are currently not allowed to
    // visit (e.g. previous step not completed yet), we will replace the current
    // history state with the minimum step we are allowed to visit.
    // This will switch the URL hash to the correct value and removes the
    // invalid URL from the history. The browser back button will now navigate
    // to the previous valid URL.
    setTimeout(() => {
      const hashStr = step > 0 ? `#${step}` : ' ';
      window.history.replaceState('', '', hashStr);
      setCurHash(hashStr);
    }, 0);
  }

  return renderStep(step, state);
};

const getLocationHash = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.hash;
};
